import React, {useCallback, useEffect, useMemo, useState} from "react";

import {fetchSelectOptionsByContractNO} from "../../../../backendInteraction/ApiFetch";
import {numberValidator} from "../../../../utils/Validator";
import {Button, Input, message, Select, Space, Table} from "antd";


import {ColumnsType} from "antd/es/table";
import {TrainingData} from "../../../../types/TrainingData";
import {
    addTrainingRow,
    deleteTrainingByTrId,
    fetchTrainingByPid,
    updateTrainingRow
} from "../../../../backendInteraction/TrainingApiFetch";

interface TrainingTableProps {
    data: TrainingData[];
    plodId: number;
    contractNo: string;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
}

const TrainingTable: React.FC<TrainingTableProps> = ({ data, plodId, contractNo, isView, isAddNewRow, setIsAddNewRow }) => {
    const [trainingData, setTrainingData] = useState<TrainingData[]>([]);
    const [nameOptions, setNameOptions] = useState<{ value: string; label: string }[]>([]);
    const [editingTrId, setEditingTrId] = useState<number | null>(null);
    const [editData, setEditData] = useState<TrainingData | null>(null);

    const loadNames = useCallback(async () => {
        if (contractNo) {
            const items = await fetchSelectOptionsByContractNO(contractNo, "Name");
            setNameOptions(items.map(item => ({ value: item, label: item })));
        } else {
            setNameOptions([]);
        }
    }, [contractNo]);

    useEffect(() => {
        loadNames();
    }, [loadNames]);

    useEffect(() => {
        setTrainingData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingTrId !== -1) {
            const newRow: TrainingData = {
                key: -1,
                tr_id: -1,
                details: "",
                instructor: "",
                trainee: "",
                time: "",
                pid: plodId ?? -1,
            };
            setTrainingData(prevData => {
                const hasNewRow = prevData.some(item => item.tr_id === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            });

            setEditingTrId(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingTrId]);

    useEffect(() => {
        if (isView) {
            if (editingTrId === -1) {
                setTrainingData(prevData => prevData.filter(item => item.tr_id !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }

            setEditingTrId(null);
            setEditData(null);
        }
    }, [isView]);

    const saveChanges = useCallback(async () => {
        console.log("Saving changes:", editData);

        if (!editData) return;

        const { time } = editData;

        if (!!time) {
            const isNumber = numberValidator(time, 'Time');
            if (!isNumber) return;
            const hoursVal = parseFloat(time);
            if (hoursVal < 0 || hoursVal > 24) {
                message.error('The "Time" value is invalid. Please enter the hours number between 0 and 24.');
                return;
            }
            if (hoursVal > 12) {
                message.info('The duration of this training exceeds 12 hours!');
            }
        }

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (editingTrId === -1) {
            const success = await addTrainingRow(editData, aid);
            if (success) {
                setTrainingData(await fetchTrainingByPid(plodId ?? -1, aid));
                message.success("New training added successfully!");
            } else {
                message.error("Failed to add new training.");
            }
        } else {
            const success = await updateTrainingRow(editingTrId ?? -1, editData);
            if (success) {
                setTrainingData(await fetchTrainingByPid(plodId ?? -1, aid));
                message.success("Training updated successfully!");
            } else {
                message.error("Failed to update training.");
            }
        }

        if (editingTrId === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingTrId(null);
        setEditData(null);
    }, [editData, editingTrId, setIsAddNewRow]);

    const cancelEditing = useCallback((record: TrainingData) => {
        console.log("Cancel editing");
        setEditingTrId(null);
        setEditData(null);

        if (record.tr_id === -1) {
            setTrainingData(prevData => prevData.filter(item => item.tr_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: TrainingData) => {
        console.log("Editing row:", record);

        if (editingTrId === -1) {
            setTrainingData(prevData => prevData.filter(item => item.tr_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingTrId(prevId => {
            if (prevId !== null) cancelEditing({ tr_id: prevId } as TrainingData);
            return record.tr_id;
        });

        setEditData({ ...record });
    }, [editingTrId, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: TrainingData) => {
        console.log("Deleting row:", record);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        const success = await deleteTrainingByTrId(record.tr_id);
        if (success) {
            setTrainingData(await fetchTrainingByPid(plodId ?? -1, aid));
            message.success("Training deleted successfully!");
        } else {
            message.error("Failed to delete training.");
        }
    }, []);

    const handleInputChange = useCallback((key: keyof TrainingData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);

    const columns: ColumnsType<TrainingData> = useMemo(() => [
        {
            title: "Details",
            dataIndex: "details",
            key: "details",
            width: "45%",
            render: (_, record) =>
                editingTrId === record.tr_id ? (
                    <Input
                        value={editData?.details}
                        onChange={(e) => handleInputChange("details", e.target.value)}
                    />
                ) : (
                    record.details
                ),
        },
        {
            title: "Instructor",
            dataIndex: "instructor",
            key: "instructor",
            width: "15%",
            minWidth: 280,
            render: (_, record) =>
                editingTrId === record.tr_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={nameOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.instructor || ""}
                        onChange={(value) => handleInputChange("instructor", value || "")}
                    />
                ) : (
                    record.instructor
                ),
        },
        {
            title: "Trainee",
            dataIndex: "trainee",
            key: "trainee",
            width: "15%",
            minWidth: 280,
            render: (_, record) =>
                editingTrId === record.tr_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={nameOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.trainee || ""}
                        onChange={(value) => handleInputChange("trainee", value || "")}
                    />
                ) : (
                    record.trainee
                ),
        },
        {
            title: "Time",
            dataIndex: "time",
            key: "time",
            width: "10%",
            render: (_, record) =>
                editingTrId === record.tr_id ? (
                    <Input
                        value={editData?.time}
                        onChange={(e) => handleInputChange("time", e.target.value)}
                    />
                ) : (
                    record.time
                ),
        },
        ...(!isView
            ? [
                {
                    title: "Action",
                    key: "action",
                    width: "15%",
                    render: (_, record) => (
                        <Space size="small">
                            <Button color="geekblue" variant="text"
                                    onClick={() => (editingTrId === record.tr_id ? saveChanges() : startEditing(record))}>
                                {editingTrId === record.tr_id ? "Save" : "Edit"}
                            </Button>
                            <Button color="geekblue" variant="text"
                                    onClick={() => (editingTrId === record.tr_id ? cancelEditing(record) : deleteRow(record))}>
                                {editingTrId === record.tr_id ? "Cancel" : "Delete"}
                            </Button>
                        </Space>
                    ),
                },
            ] as ColumnsType<TrainingData>
            : []),
    ], [isView, nameOptions, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<TrainingData>
            columns={columns}
            pagination={false}
            dataSource={trainingData}
        />
    );
};

export default TrainingTable;